import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { normalizePhone } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardList, Loader2, FileDown, Truck, CalendarClock } from "lucide-react";

const SERVICE_LEVELS = ["Standard", "Guaranteed", "GuaranteedByNoon", "Expedited"];
const PAYMENT_TERMS = ["Prepaid", "Collect"];

function commoditiesFromQuote(q) {
  if (q?.line_items?.length) {
    return q.line_items.map((li) => ({
      desc: li.description || li.product_name || "Freight",
      cls: (li.fak_applied ? li.fak_class : li.freight_class) || li.freight_class || q.calculated_class || "—",
      pieces: li.pieces || li.units || 1,
      weight: li.weight || 0,
      type: li.handling_unit_type || "Pallet",
    }));
  }
  const s = q?.stack_result || {};
  const fak = q?.fak || {};
  return [{
    desc: q?.product_name || "Freight",
    cls: (fak.applied ? fak.fak_class : q?.calculated_class) || "—",
    pieces: q?.quantity || 1,
    weight: s.total_cargo_weight || 0,
    type: "Pallet",
  }];
}

function Fld({ label, value, onChange, className = "", testid, placeholder, required, error }) {
  return (
    <div className={className}>
      <Label className="label-caps">{label}{required && <span className="text-[hsl(var(--destructive))]"> *</span>}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className={`mt-1 h-9 ${error ? "border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]" : ""}`} />
      {error && (
        <p className="mt-1 text-xs font-medium text-[hsl(var(--destructive))]" data-testid={testid ? `${testid}-error` : undefined}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function BolDialog({ open, onOpenChange, quote, onGenerated }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const commodities = useMemo(() => commoditiesFromQuote(quote), [quote]);

  // Flag a non-empty phone that R+L would reject ("Invalid phone number" 400).
  const shipperPhoneBad = !!form.shipper_phone?.trim() && !normalizePhone(form.shipper_phone);
  const consigneePhoneBad = !!form.consignee_phone?.trim() && !normalizePhone(form.consignee_phone);

  useEffect(() => {
    if (open && quote) {
      const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
      const qd = (quote.pickup_date || "").slice(0, 10);
      setForm({
        shipper_name: quote.origin_company || "Premier Handling Solutions",
        shipper_address: quote.origin_address1 || "",
        shipper_city: quote.origin_city || "",
        shipper_state: quote.origin_state || "",
        shipper_zip: quote.origin_zip || "",
        shipper_phone: quote.origin_contact_phone || "",
        consignee_name: quote.dest_company || quote.customer_name || "",
        consignee_address: quote.dest_address1 || "",
        consignee_city: quote.dest_city || "",
        consignee_state: quote.dest_state || "",
        consignee_zip: quote.destination_zip || "",
        consignee_contact: quote.dest_contact_name || "",
        consignee_phone: quote.dest_contact_phone || "",
        billto_name: "Premier Handling Solutions",
        billto_address: "1415 Davis Rd",
        billto_city: "Elgin",
        billto_state: "IL",
        billto_zip: "60123",
        service_level: "Standard",
        payment_terms: "Prepaid",
        special_instructions: quote.notes || "",
        po_number: quote.quote_ref || "",
        pickup_number: "",
        generate_universal_pro: false,
        schedule_pickup: true,
        pickup_date: qd >= today ? qd : today,
        pickup_ready_time: "09:00",
        pickup_close_time: "17:00",
        pickup_instructions: "",
        pickup_contact_name: quote.origin_contact_name || "",
        pickup_contact_company: quote.origin_company || "Premier Handling Solutions",
        pickup_contact_phone: quote.origin_contact_phone || "",
        pickup_contact_email: "",
        pickup_email_confirmation: true,
      });
    }
  }, [open, quote]);

  const generate = async () => {
    if (!form.shipper_phone?.trim()) return toast.error("Shipper phone number is required by R+L.");
    if (shipperPhoneBad) return toast.error("Shipper phone isn't a valid 10-digit US number.");
    if (!form.consignee_address?.trim()) return toast.error("Consignee street address is required.");
    if (consigneePhoneBad) return toast.error("Consignee phone isn't a valid 10-digit US number — correct it or clear it.");
    if (form.schedule_pickup && !form.pickup_date) return toast.error("Choose a pickup date to schedule the pickup.");
    setBusy(true);
    try {
      const res = await api.post(`/quotes/${quote.id}/bol`, form, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      toast.success(form.schedule_pickup
        ? "Shipment booked — R+L BOL created and pickup scheduled."
        : "R+L Bill of Lading created (document only).");
      onOpenChange(false);
      onGenerated?.(blobUrl);
    } catch (e) {
      // error body is a blob when responseType=blob — read it
      let msg = apiError(e);
      try {
        const text = await e?.response?.data?.text?.();
        if (text) { const j = JSON.parse(text); if (j.detail) msg = j.detail; }
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="bol-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ClipboardList className="h-5 w-5 text-[hsl(var(--primary))]" /> Book R+L Shipment — {quote?.quote_number}
          </DialogTitle>
          <DialogDescription>
            {form.schedule_pickup ? (
              <>Creates the real R+L BOL <span className="font-semibold text-foreground">AND schedules the pickup — a truck will be dispatched</span> to the shipper&apos;s dock.</>
            ) : (
              <>Creates the real R+L Carriers BOL (a web pro number). <span className="font-semibold text-foreground">No pickup is scheduled</span> — this only generates the document.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {quote?.bol_pro_number && (
          <div className="rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-3 py-2 text-sm" data-testid="bol-existing-pro">
            A BOL already exists for this quote — R+L Pro <span className="font-bold">{quote.bol_pro_number}</span>. Generating again will create a new pro.
          </div>
        )}

        <div className="space-y-5">
          {/* ---- Pickup scheduling (real booking) ---- */}
          <div className={`rounded-md border p-3 transition-colors ${form.schedule_pickup ? "border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/5" : "border-border"}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CalendarClock className={`h-4 w-4 ${form.schedule_pickup ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`} />
                <div>
                  <div className="text-sm font-semibold">Schedule pickup with R+L</div>
                  <div className="text-xs text-muted-foreground">
                    {form.schedule_pickup ? "A truck will be dispatched — this is a real booking." : "Off = BOL document only, no truck dispatched."}
                  </div>
                </div>
              </div>
              <Switch checked={!!form.schedule_pickup} onCheckedChange={(v) => set("schedule_pickup", v)} data-testid="bol-schedule-pickup" />
            </div>
            {form.schedule_pickup && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <Label className="label-caps">Pickup Date <span className="text-[hsl(var(--destructive))]">*</span></Label>
                    <Input type="date" value={form.pickup_date || ""} min={new Date().toISOString().slice(0, 10)} onChange={(e) => set("pickup_date", e.target.value)} data-testid="bol-pickup-date" className="mt-1 h-9" />
                  </div>
                  <div>
                    <Label className="label-caps">Dock Ready</Label>
                    <Input type="time" value={form.pickup_ready_time || ""} onChange={(e) => set("pickup_ready_time", e.target.value)} data-testid="bol-pickup-ready" className="mt-1 h-9" />
                  </div>
                  <div>
                    <Label className="label-caps">Dock Close</Label>
                    <Input type="time" value={form.pickup_close_time || ""} onChange={(e) => set("pickup_close_time", e.target.value)} data-testid="bol-pickup-close" className="mt-1 h-9" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Fld label="Pickup Contact" value={form.pickup_contact_name} onChange={(v) => set("pickup_contact_name", v)} testid="bol-pickup-contact" placeholder="Dock Office" />
                  <Fld label="Contact Phone" value={form.pickup_contact_phone} onChange={(v) => set("pickup_contact_phone", v)} testid="bol-pickup-phone" placeholder="defaults to shipper phone" />
                  <Fld label="Contact Email" value={form.pickup_contact_email} onChange={(v) => set("pickup_contact_email", v)} testid="bol-pickup-email" placeholder="optional" />
                </div>
                <Fld label="Pickup Instructions (for the driver)" value={form.pickup_instructions} onChange={(v) => set("pickup_instructions", v)} testid="bol-pickup-instructions" placeholder="e.g. Check in at dock 4, forklift on site" />
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Checkbox checked={!!form.pickup_email_confirmation} onCheckedChange={(v) => set("pickup_email_confirmation", !!v)} data-testid="bol-pickup-email-confirm" />
                  Have R+L email the pickup confirmation
                </label>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label className="label-caps">Service Level</Label>
              <Select value={form.service_level} onValueChange={(v) => set("service_level", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="bol-service-level"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICE_LEVELS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Freight Charges</Label>
              <Select value={form.payment_terms} onValueChange={(v) => set("payment_terms", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="bol-payment-terms"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Pro Number Type</Label>
              <Select value={form.generate_universal_pro ? "universal" : "web"} onValueChange={(v) => set("generate_universal_pro", v === "universal")}>
                <SelectTrigger className="mt-1 h-9" data-testid="bol-pro-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="web">Web Pro (R+L stickers)</SelectItem>
                  <SelectItem value="universal">Universal Pro (you sticker)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Fld label="PO Number" value={form.po_number} onChange={(v) => set("po_number", v)} testid="bol-po-number" placeholder="prints on the BOL (defaults to quote ref)" />
            <Fld label="Pickup Number" value={form.pickup_number} onChange={(v) => set("pickup_number", v)} testid="bol-pickup-number" placeholder="customer / warehouse pickup #" />
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="label-caps text-[hsl(var(--primary))]">Ship From (Origin)</div>
              <Fld label="Company" value={form.shipper_name} onChange={(v) => set("shipper_name", v)} testid="bol-shipper-name" required />
              <Fld label="Street Address" value={form.shipper_address} onChange={(v) => set("shipper_address", v)} testid="bol-shipper-address" />
              <div className="grid grid-cols-3 gap-2">
                <Fld label="City" value={form.shipper_city} onChange={(v) => set("shipper_city", v)} />
                <Fld label="State" value={form.shipper_state} onChange={(v) => set("shipper_state", v)} />
                <Fld label="ZIP" value={form.shipper_zip} onChange={(v) => set("shipper_zip", v)} />
              </div>
              <Fld label="Phone" value={form.shipper_phone} onChange={(v) => set("shipper_phone", v)} testid="bol-shipper-phone" placeholder="10 digits" required
                error={shipperPhoneBad ? "Enter a valid 10-digit US phone." : undefined} />
            </div>

            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="label-caps text-[hsl(var(--success))]">Ship To (Consignee)</div>
              <Fld label="Company" value={form.consignee_name} onChange={(v) => set("consignee_name", v)} testid="bol-consignee-name" required />
              <Fld label="Street Address" value={form.consignee_address} onChange={(v) => set("consignee_address", v)} testid="bol-consignee-address" required />
              <div className="grid grid-cols-3 gap-2">
                <Fld label="City" value={form.consignee_city} onChange={(v) => set("consignee_city", v)} />
                <Fld label="State" value={form.consignee_state} onChange={(v) => set("consignee_state", v)} />
                <Fld label="ZIP" value={form.consignee_zip} onChange={(v) => set("consignee_zip", v)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Fld label="Attention" value={form.consignee_contact} onChange={(v) => set("consignee_contact", v)} />
                <Fld label="Phone" value={form.consignee_phone} onChange={(v) => set("consignee_phone", v)} testid="bol-consignee-phone"
                  placeholder="10 digits"
                  error={consigneePhoneBad ? "Not a valid 10-digit US phone — fix or clear it (the shipper phone will be used)." : undefined} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Fld label="Bill Charges To" value={form.billto_name} onChange={(v) => set("billto_name", v)} testid="bol-billto-name" />
            <Fld label="Bill-To Street" value={form.billto_address} onChange={(v) => set("billto_address", v)} testid="bol-billto-address" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <Fld label="Bill-To City" value={form.billto_city} onChange={(v) => set("billto_city", v)} className="sm:col-span-2" />
            <Fld label="ST" value={form.billto_state} onChange={(v) => set("billto_state", v)} />
            <Fld label="ZIP" value={form.billto_zip} onChange={(v) => set("billto_zip", v)} />
          </div>

          {/* Commodities (from the quote — declared on the BOL) */}
          <div className="rounded-md border border-border">
            <div className="flex items-center gap-2 border-b border-border px-3 py-2 label-caps">
              <Truck className="h-4 w-4 text-muted-foreground" /> Commodities (from this quote)
            </div>
            <div className="divide-y divide-border" data-testid="bol-commodities">
              {commodities.map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="truncate font-medium">{c.desc}</span>
                  <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                    {c.pieces} {c.type} · {Math.round(c.weight)} lb · Class {c.cls}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="label-caps">Special Instructions</Label>
            <Textarea value={form.special_instructions || ""} onChange={(e) => set("special_instructions", e.target.value)} rows={2} className="mt-1" data-testid="bol-instructions" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={generate} disabled={busy || shipperPhoneBad || consigneePhoneBad} data-testid="bol-generate" className={`gap-2 text-white ${form.schedule_pickup ? "bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90" : "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : form.schedule_pickup ? <Truck className="h-4 w-4" /> : <FileDown className="h-4 w-4" />}
            {form.schedule_pickup ? "Book & Schedule Pickup" : "Generate BOL only"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
